import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '@core/components/toasts/toasts.service';
import { FileService } from '@core/services/file.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ConfirmDialogComponent,
  ConfirmDialogEnum,
} from 'app/layout/components/confirm-dialog/confirm-dialog.component';
import { BaseEnum } from 'services/enums/baseEnum';
import { ActionPlanService } from 'services/corporate/action-plan/action-plan.service';
import { EmployeeService } from 'services/corporate/employee.service';
import { EthicsChannelReportService } from 'services/ethics-channel/ethics-channel-report.service';
import { ActionPlanModel } from 'services/models/corporate/action-plan/action-plan.model';
import { EmployeeModel } from 'services/models/employee.model';
import { ActionPlanStatus } from 'services/models/corporate/action-plan/enum/ActionPlanStatus.enum';
import { FileContentModel } from 'services/models/fileContent';
import { WCKOccurrenceService } from 'services/workcheck/wck-occurrence.service';
import { QueryParams } from 'services/models/odata/queryParams';
import { Filter } from 'services/models/odata/filter';

type LoadingStates = {
  actions: boolean;
};

const LOADING_STATES: LoadingStates = {
  actions: false,
};

interface TreatActionFormValue {
  id: string;
  status: string | null;
  resolutionDescription: string | null;
  fileContent: FileContentModel | null;
}

interface DropzoneFileChange {
  files?: Array<{ file: File }>;
}

@Component({
  selector: 'app-treat-action-plan-drawer',
  templateUrl: './treat-action-plan-drawer.component.html',
  styleUrls: ['./treat-action-plan-drawer.component.scss'],
})
export class TreatActionPlanDrawerComponent implements OnInit {
  @Input() actionPlanId?: string;
  @Input() referenceId?: string;
  @Input() filterByEmployee?: boolean = false;
  @Input() closeWhenFinished?: boolean = false;

  public loadingStates: LoadingStates = { ...LOADING_STATES };
  /** Apenas IN_PROGRESS e COMPLETED permitidos no tratamento da ação */
  public actionStatusOptions: BaseEnum[] = ActionPlanStatus.buildListEnum().filter(
    s =>
      s.value === ActionPlanStatus.IN_PROGRESS.value || s.value === ActionPlanStatus.COMPLETED.value
  );
  public actionsList: ActionPlanModel[] = [];
  public form: FormGroup;

  constructor(
    private modalService: NgbActiveModal,
    private _actionPlanService: ActionPlanService,
    private employeeService: EmployeeService,
    private fileService: FileService,
    public _modalService: NgbModal,
    public _toasterService: ToastService,
    public wckOcurrenceService: WCKOccurrenceService,
    public ethicsChannelService: EthicsChannelReportService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.buildForm();

    this.loadActions();

    this.getActionPlan();
  }

  private getActionPlan(): void {
    if (!this.actionPlanId) return;

    this.setLoadingState('actions', true);

    this._actionPlanService.get(this.actionPlanId, true).subscribe({
      next: res => this.handleActionsLoadWithEmployees([res.data]),
      error: () => this.setLoadingState('actions', false),
    });
  }

  private loadActions(): void {
    if (!this.referenceId) return;

    this.setLoadingState('actions', true);

    this._actionPlanService.listByReferenceId(this.referenceId, this.filterByEmployee).subscribe({
      next: res => this.handleActionsLoadWithEmployees(res.data),
      error: () => this.setLoadingState('actions', false),
    });
  }

  public onClose(): void {
    this.modalService.close();
  }

  public handleSubmit(index: number): void {
    const payload = this.getPayload(index);
    const isCompleting = payload.status === ActionPlanStatus.COMPLETED.value;
    const message = isCompleting
      ? `
        Você está prestes a <b>enviar a resolução</b> da
        <b>Ação ${index + 1} - ${payload.typeTitle}</b>.<br><br>
        Após confirmar, esta ação será marcada como <b>finalizada</b>
        e não poderá ser alterada.<br><br>
        Deseja continuar?
      `
      : `
        Você está prestes a <b>salvar a resolução</b> da
        <b>Ação ${index + 1} - ${payload.typeTitle}</b>.<br><br>
        A ação permanecerá como <b>em andamento</b> e poderá ser alterada posteriormente.<br><br>
        Deseja continuar?
      `;

    const modalRef = this._modalService.open(ConfirmDialogComponent);
    modalRef.componentInstance.type = ConfirmDialogEnum.SWITCH_ALERT;
    modalRef.componentInstance.messageConfirm = 'Sim, enviar';
    modalRef.componentInstance.messageCancel = 'Cancelar';
    modalRef.componentInstance.icon = 'info';
    modalRef.componentInstance.message = message;

    modalRef.result
      .then((confirmed: boolean) => {
        if (!confirmed) return;

        this._toasterService.showSuccess('Enviando resolução da ação, aguarde...');

        this._actionPlanService.finishAction(payload).subscribe({
          next: () => {
            this._toasterService.showSuccess('Resolução enviada com sucesso! Ações atualizadas.');
            const shouldClose = this.closeWhenFinished || this.actionsList.length === 1;
            if (shouldClose) {
              this.modalService.close({ onRefetch: true, actionId: payload.id });
            } else {
              this.loadActions();
            }
          },
          error: () => {
            this._toasterService.showError(
              'Não foi possível concluir a ação. Tente novamente mais tarde.'
            );
          },
        });
      })
      .catch(() => {});
  }

  private getPayload(index: number): ActionPlanModel {
    const { id, status, fileContent, resolutionDescription } = this.getActionFormValue(index);

    const action = this.actionsList.find(x => x.id === id);
    const payload = {
      ...action,
      id,
      status: status ?? undefined,
      fileContent,
      resolutionDescription: resolutionDescription ?? undefined,
    };

    return payload as ActionPlanModel;
  }

  private setLoadingState(key: keyof typeof LOADING_STATES, value: boolean) {
    this.loadingStates[key] = value;
  }

  public get drawerTitle(): string {
    return 'Resolução das Ações';
  }

  public trackByActionId(index: number, action: ActionPlanModel): string {
    return action.id;
  }

  public async onFileSelectionChanged(fileData: DropzoneFileChange, index: number): Promise<void> {
    const fileContent = await this.processSelectedFile(fileData);
    // pega o FormGroup baseado no índice
    const actionForm = this.actions.at(index) as FormGroup;

    // agora você pode atualizar o campo "fileContent"
    actionForm.patchValue({ fileContent });
  }

  public onGetFile(index: number): void {
    const id = this.getActionForm(index).get('id')?.value as string;
    this._actionPlanService.getActionFile(id);
  }

  private async processSelectedFile(fileData: DropzoneFileChange): Promise<FileContentModel> {
    let fileContent: FileContentModel = {
      fileName: '',
      extension: '',
      encodedBase64: '',
    };

    if (fileData?.files?.length) {
      const file = fileData.files[0]?.file;
      const stringBase64 = (await this.fileService.fileToBase64(file)) as string;
      const [, encodedBase64] = stringBase64.split('base64,');

      fileContent = {
        fileName: file.name,
        extension: file.name.split('.').pop() || '',
        encodedBase64,
      };
    }

    return fileContent;
  }

  private buildForm(): void {
    this.form = this.fb.group({
      actions: this.fb.array([]),
    });
  }

  private populateActionsForm(actions: ActionPlanModel[]): void {
    this.actions.clear();
    if (!actions || !actions.length) return;

    actions.forEach(action => {
      const group = this.fb.group({
        id: [action?.id],
        status: [
          action === null || action.status === ActionPlanStatus.PENDING.value
            ? null
            : action.status,
          [Validators.required],
        ],
        resolutionDescription: [action?.resolutionDescription ?? null, [Validators.required]],
        fileContent: [
          {
            fileName: action.resolutionFileName,
            fileKey: action.resolutionFileKey,
          } as FileContentModel,
        ],
      });

      if (action.status === ActionPlanStatus.COMPLETED.value) {
        group.disable();
      }

      this.actions.push(group);
    });
  }

  public get actions(): FormArray {
    return this.form.get('actions') as FormArray;
  }

  public fileContent(index: number): FileContentModel | null {
    return (this.getActionForm(index).get('fileContent')?.value as FileContentModel) ?? null;
  }

  public isCompleted(index: number): boolean {
    const id = this.getActionForm(index).get('id')?.value as string;
    const action = this.actionsList.find(x => x.id === id);
    return action?.status === ActionPlanStatus.COMPLETED.value;
  }

  public getActionForm(index: number): FormGroup {
    return this.actions.at(index) as FormGroup;
  }

  private getActionFormValue(index: number): TreatActionFormValue {
    return this.getActionForm(index).value as TreatActionFormValue;
  }

  private mapActionPlan(item: ActionPlanModel, employeeMap: Map<string, string>): ActionPlanModel {
    const responsibleName = employeeMap.get(item.responsibleId) ?? item.responsibleName ?? '';
    return {
      ...item,
      typeId: item.typeId,
      typeTitle: item.typeTitle,
      responsible: {
        id: item.responsibleId,
        name: responsibleName,
      },
    } as ActionPlanModel;
  }

  private handleActionsLoadWithEmployees(actions: ActionPlanModel[]): void {
    this.getEmployeesByIds(actions)
      .then(employees => {
        const employeeMap = new Map(employees.map(e => [e.id, e.name]));
        this.actionsList = actions.map(a => this.mapActionPlan(a, employeeMap));
        this.populateActionsForm(this.actionsList);
      })
      .catch(() => {
        this.actionsList = actions.map(a => this.mapActionPlan(a, new Map()));
        this.populateActionsForm(this.actionsList);
      })
      .finally(() => this.setLoadingState('actions', false));
  }

  private async getEmployeesByIds(actions: ActionPlanModel[]): Promise<EmployeeModel[]> {
    const responsibleIds = (actions ?? []).map(a => a.responsibleId).filter(id => !!id);
    const employeeIds = Array.from(new Set(responsibleIds));

    if (!employeeIds.length) return [];

    const filter = Filter.multEqualString('id', employeeIds);
    const params: QueryParams = {
      select: ['id', 'name'],
      filter,
    } as QueryParams;

    const response = await this.employeeService
      .customList<EmployeeModel>(params, 'odata', EmployeeModel, true)
      .toPromise();

    return response?.data ?? [];
  }

  public get activeIds(): string {
    if (!this.actionsList?.length) return 'null';

    if (this.actionsList.length === 1) return 'panel-0';

    if (!this.actionPlanId) return 'null';

    const index = this.actions.controls.findIndex(
      group => group.get('id')?.value === this.actionPlanId
    );

    return index !== -1 ? `panel-${index}` : 'null';
  }
}
